// Calculation of target bonus valid in one year
// Input: year, contract changes, targetbonus items, startDate, endDate?

///////////////////////////////////////////////////////////////////////////////////////////////////////////
//                                                                                                       //
//  These methods are the same used in the BE for the calculation of the pro rata and the annual bonus,  //
//  in the flexible-prorata-management-helper.ts file.                                                   //
//  If you make change, remember to update the methods in both files.                                    //
//                                                                                                       //
///////////////////////////////////////////////////////////////////////////////////////////////////////////

import moment, { Moment } from 'moment-timezone'
import { ContractChangeFragment, SalaryOfYearFragment, TargetBonusOfYearFragment, CurrencyConversionDto, Currency } from '../../../../types'
import { retrieveCurrencyConversionRate } from '../../../../shared/currencyConversionUtil'

type FullAmountAndMonthAndYear = {
  amount: number
  month: number
  year: number
  salaryAmount: number
  changeStart: string
  changeEnd?: string
  annualAmount: number
}

export type ProrataPartial = {
  from: string
  amount: number
  annualAmount: number
}

type ProrataCalc = {
  amount: number
  partials: ProrataPartial[]
  flexibleBonusAmount?: number
}

export type FlexibleBonusOverride = {
  bonusAmount: number
  contractId: number
}

type TargetBonusWithContractId = {
  contractId: number
  fromDate: TargetBonusOfYearFragment['fromDate']
  toDate?: TargetBonusOfYearFragment['toDate']
}

type ContractChangeWithBonusCheck = {
  changes: string
  validFrom?: string | null
}

export type ContractWithValidity = {
  id: number
  validFrom: string | Date
  validTo?: string | Date | null
  cancellationTo?: string | Date | null
}

const getEffectiveContractEnd = (contract: ContractWithValidity): Moment | null => {
  const effectiveEnd = contract.validTo ?? contract.cancellationTo
  return effectiveEnd ? moment.utc(effectiveEnd) : null
}

export const getActiveContractsInYear = (
  employeeTimezone: string,
  year: number,
  contracts: ContractWithValidity[],
): ContractWithValidity[] => {
  const yearStart = moment.utc([year, 0, 1]).tz(employeeTimezone).startOf('day')
  const yearEnd = moment.utc([year, 11, 31]).tz(employeeTimezone).endOf('day')

  return contracts.filter((contract) => {
    const from = moment.utc(contract.validFrom).tz(employeeTimezone).startOf('day')
    const to = getEffectiveContractEnd(contract)?.tz(employeeTimezone).endOf('day') ?? null

    return from.isSameOrBefore(yearEnd, 'day') && (to === null || to.isSameOrAfter(yearStart, 'day'))
  })
}

export const getActiveTargetBonusesInYear = (
  employeeTimezone: string,
  year: number,
  targetBonusList: TargetBonusWithContractId[],
): TargetBonusWithContractId[] => {
  const yearStart = moment.utc([year, 0]).tz(employeeTimezone)
  const yearEnd = moment.utc([year, 11]).tz(employeeTimezone)

  return targetBonusList.filter((targetBonus) => {
    const from = moment.utc(targetBonus.fromDate).tz(employeeTimezone)
    const to = targetBonus.toDate ? moment.utc(targetBonus.toDate).tz(employeeTimezone) : null
    return from.isSameOrBefore(yearEnd, 'month') && (to === null || to.isSameOrAfter(yearStart, 'month'))
  })
}

export const hasContractChangeWithBonusForYear = (
  contractChangeList: ContractChangeWithBonusCheck[],
  referenceYear: number,
): boolean => {
  return contractChangeList.some((contractChange) => {
    const changes = JSON.parse(contractChange.changes)
    if (changes.bonus === undefined) {
      return false
    }
    if (!contractChange.validFrom) {
      return true
    }
    const fromDate = moment(contractChange.validFrom)
    return fromDate.year() === referenceYear || fromDate.year() === referenceYear - 1
  })
}

export const canUseFlexibleBonusAsReference = (
  employeeTimezone: string,
  year: number,
  contractsInYear: ContractWithValidity[],
  targetBonusList: TargetBonusWithContractId[],
  contractChanges: ContractChangeWithBonusCheck[],
): boolean => {
  const activeContracts = getActiveContractsInYear(employeeTimezone, year, contractsInYear)
  if (activeContracts.length !== 1) {
    return false
  }

  const activeTargetBonuses = getActiveTargetBonusesInYear(employeeTimezone, year, targetBonusList)
  if (activeTargetBonuses.length !== 1) {
    return false
  }

  if (activeTargetBonuses[0].contractId !== activeContracts[0].id) {
    return false
  }

  return !hasContractChangeWithBonusForYear(contractChanges, year)
}

const shouldUseFlexibleBonusAmount = (
  employeeTimezone: string,
  year: number,
  contractsInYear: ContractWithValidity[],
  targetBonusList: TargetBonusWithContractId[],
  contractChanges: ContractChangeWithBonusCheck[],
  flexibleBonusOverride: FlexibleBonusOverride,
): boolean => {
  if (!canUseFlexibleBonusAsReference(employeeTimezone, year, contractsInYear, targetBonusList, contractChanges)) {
    return false
  }

  return activeTargetBonusesMatchFlexibleBonusContract(
    employeeTimezone,
    year,
    targetBonusList,
    flexibleBonusOverride.contractId,
  )
}

const activeTargetBonusesMatchFlexibleBonusContract = (
  employeeTimezone: string,
  year: number,
  targetBonusList: TargetBonusWithContractId[],
  flexibleBonusContractId: number,
): boolean => {
  const activeTargetBonuses = getActiveTargetBonusesInYear(employeeTimezone, year, targetBonusList)
  return activeTargetBonuses.length === 1 && activeTargetBonuses[0].contractId === flexibleBonusContractId
}

export const getFlexibleBonusProrataPartialFrom = (employeeTimezone: string, year: number): string =>
  moment.tz(`${year}-01-01`, 'YYYY-MM-DD', employeeTimezone).toISOString()

export const calculateBonusWithSalary = (
  employeeTimezone: string,
  isBonusValidationEnabled: boolean,
  year: number,
  changes: ContractChangeFragment[] | undefined = [],
  targetBonusList: TargetBonusOfYearFragment[],
  salaryList: SalaryOfYearFragment[],
  currentSalary: number,
  currentBonus: number,
  currencyConversionData: CurrencyConversionDto[],
  currentContractCurrency: Currency,
  contractStart: string,
  contractEnd?: string,
  contractsInYear: ContractWithValidity[] = [],
  flexibleBonusOverride?: FlexibleBonusOverride,
): ProrataCalc => {
  // adapted to the js getMonth() function results where DEZ is 11
  const monthInYear = 11
  const startDate = moment.utc(contractStart).tz(employeeTimezone)
  const endDate = contractEnd ? moment.utc(contractEnd).tz(employeeTimezone) : undefined
  const firstMonth = getFirstMonthOfTargetsForYear(employeeTimezone, year, targetBonusList, startDate)
  const lastMonth = endDate && endDate.year() === year ? endDate.month() : monthInYear

  // parsing and filtering contract change blocks
  const filteredContractChanges: FullAmountAndMonthAndYear[] = filterAndTransformContractChanges(
    employeeTimezone,
    changes,
    currentSalary,
    year,
  )

  // create calculationBlocks from Changes
  const contractChangesBlockList: FullAmountAndMonthAndYear[] = createBlockListFromChanges(filteredContractChanges, year)

  const adjustedTargetBonusList: TargetBonusOfYearFragment[] = convertTargetBonusAmountsToCurrentContractOne(
    targetBonusList,
    currencyConversionData,
    currentContractCurrency,
  )

  // create calculationBlocks from Contract Bonus Elements
  let contractBonusBlockList: FullAmountAndMonthAndYear[] = createBlocksFromContractBonus(employeeTimezone, adjustedTargetBonusList, year)

  if (salaryList && salaryList.length > 0) {
    contractBonusBlockList = mergeSalaryToBlocks(employeeTimezone, contractBonusBlockList, salaryList, year)
  }

  // combine lists by filling in the contract parts
  for (const contractBonusBlock of contractBonusBlockList) {
    const contractChangeBlockInTheSameMonth = contractChangesBlockList.find(
      (contractChangeBlock) => contractChangeBlock.month === contractBonusBlock.month,
    )
    if (!contractChangeBlockInTheSameMonth) {
      contractChangesBlockList.push(contractBonusBlock)
    } else if (isBonusValidationEnabled && contractChangeBlockInTheSameMonth && contractChangeBlockInTheSameMonth.amount >= 0) {
      const totalA = contractBonusBlock.salaryAmount + contractBonusBlock.amount
      const totalB = contractChangeBlockInTheSameMonth.salaryAmount + contractChangeBlockInTheSameMonth.amount
      if (
        filteredContractChanges[0].amount > currentBonus
          ? contractBonusBlock.amount > contractChangeBlockInTheSameMonth.amount
          : totalA >= totalB && contractBonusBlock.salaryAmount > contractChangeBlockInTheSameMonth.salaryAmount
      ) {
        const index = contractChangesBlockList.indexOf(contractChangeBlockInTheSameMonth)
        contractChangesBlockList.splice(index, 1, contractBonusBlock)
      }
    }
  }

  // filter with start and end date
  const validCalculationBlockList: FullAmountAndMonthAndYear[] = contractChangesBlockList.filter(
    (blockElement: FullAmountAndMonthAndYear) => blockElement.month >= firstMonth && blockElement.month <= lastMonth,
  )

  // adjust first month if started after 15th
  if (startedAfter15th(employeeTimezone, startDate, year, targetBonusList, validCalculationBlockList)) {
    validCalculationBlockList[validCalculationBlockList.length - 1].amount /= 2
  }

  //calculate bonus
  const calculatedTargetBonus = validCalculationBlockList.reduce((acc, curr) => acc + curr.amount, 0)
  const partials = calculatePartials(validCalculationBlockList, employeeTimezone, year, firstMonth, monthInYear, startDate, endDate)

  const prorataCalc: ProrataCalc = {
    amount: Number(calculatedTargetBonus.toFixed(2)),
    partials: partials,
  }

  if (
    flexibleBonusOverride &&
    shouldUseFlexibleBonusAmount(employeeTimezone, year, contractsInYear, targetBonusList, changes ?? [], flexibleBonusOverride)
  ) {
    return {
      ...prorataCalc,
      flexibleBonusAmount: Number(flexibleBonusOverride.bonusAmount.toFixed(2)),
    }
  }

  return prorataCalc
}

const calculatePartials = (
  validCalculationBlockList: FullAmountAndMonthAndYear[],
  employeeTimezone: string,
  year: number,
  firstMonth: number,
  monthInYear: number,
  startDate: Moment,
  endDate?: Moment,
): ProrataPartial[] => {
  // sort
  validCalculationBlockList = validCalculationBlockList.sort((e1, e2) => e1.month - e2.month)
  // if last part of year has 0 bonus add block for this part
  const lastMonthBlock = validCalculationBlockList[validCalculationBlockList.length - 1]
  if (lastMonthBlock && lastMonthBlock.month !== monthInYear) {
    validCalculationBlockList.push({
      year,
      amount: 0,
      month: lastMonthBlock.month + 1,
      salaryAmount: 0,
      changeStart: endDate
        ? endDate.toISOString()
        : lastMonthBlock.changeEnd || moment.tz(`${year}-${lastMonthBlock.month + 2}-01`, 'YYYY-M-DD', employeeTimezone).toISOString(),
      annualAmount: 0,
    })
  }
  // create partials
  let partials = validCalculationBlockList.map((block) => {
    const partial: ProrataPartial = {
      from: block.changeStart,
      amount: block.amount,
      annualAmount: block.annualAmount,
    }
    return partial
  })
  // merge consecutive month with same annual bonus
  partials = partials.reduce((acc: ProrataPartial[], curr) => {
    const last = acc.pop()
    if (last && curr.annualAmount === last.annualAmount) {
      last.amount += curr.amount
      acc.push(last)
    } else {
      if (last) acc.push(last)
      acc.push(curr)
    }
    return acc
  }, [])
  partials = partials.map((partial) => {
    let from = moment.utc(partial.from).tz(employeeTimezone)
    // if start in last year set January as start date
    if (from.year() < year) {
      partial.from = moment.tz(`${year}-${firstMonth + 1}-01`, 'YYYY-M-DD', employeeTimezone).toISOString()
    }
    from = moment.utc(partial.from).tz(employeeTimezone)
    // if started during year set start date in first partial
    if (from.year() === startDate.year() && from.month() === startDate.month() && startDate.isAfter(from)) {
      partial.from = startDate.toISOString()
    }
    // set two digit after decimal point for amount
    partial.amount = Number(partial.amount.toFixed(2))
    partial.annualAmount = Number(partial.annualAmount.toFixed(2))
    return partial
  })
  return partials
}

const startedAfter15th = (
  employeeTimezone: string,
  startDate: Moment,
  year: number,
  targetBonusList: TargetBonusOfYearFragment[],
  validCalculationBlockList: FullAmountAndMonthAndYear[],
): boolean => {
  return (
    !!startDate &&
    startDate.year() === year &&
    startDate.date() >= 15 &&
    validCalculationBlockList.length > 0 &&
    !targetBonusList.some(
      (bonus) =>
        moment.utc(bonus.fromDate).tz(employeeTimezone).year() < year &&
        (bonus.toDate === null || moment.utc(bonus.toDate).tz(employeeTimezone).year() >= year),
    )
  )
}

export const getFirstMonthOfTargetsForYear = (
  employeeTimezone: string,
  referenceYear: number,
  targetBonusList: TargetBonusOfYearFragment[],
  contractStartDate: Moment,
): number => {
  // If referenceYear is current year we know that we are in the context of New Targets
  // and if not we know that we are in the context of Past Targets
  if (targetBonusList.length) {
    for (const { fromDate, toDate } of targetBonusList) {
      const from = moment.utc(fromDate).tz(employeeTimezone)
      const to = moment.utc(toDate).tz(employeeTimezone)
      if (
        from.year() <= referenceYear &&
        to.year() === referenceYear &&
        contractStartDate.year() === referenceYear &&
        contractStartDate.month() > to.month()
      ) {
        return from.year() === referenceYear ? from.month() : 0
      }

      // target spans from year before into wanted year
      if (from.year() < referenceYear && to.year() >= referenceYear) {
        return 0
      }
    }
  }

  return contractStartDate.year() === referenceYear ? contractStartDate.month() : 0
}

const createBlockListFromChanges = (contractChangeList: FullAmountAndMonthAndYear[], referenceYear: number) => {
  const calculationBlockList: FullAmountAndMonthAndYear[] = []
  if (contractChangeList.length > 0) {
    // This is correct because there can only be one cc relating to the bonus
    const ccToBonusInPreviousYear: FullAmountAndMonthAndYear | undefined = contractChangeList.find((cc) => cc.year === referenceYear - 1)
    if (ccToBonusInPreviousYear) {
      const change: FullAmountAndMonthAndYear = {
        month: 0,
        year: referenceYear,
        amount: ccToBonusInPreviousYear.amount,
        salaryAmount: ccToBonusInPreviousYear.salaryAmount,
        changeStart: ccToBonusInPreviousYear.changeStart,
        annualAmount: ccToBonusInPreviousYear.amount,
      }
      contractChangeList.push(change)
    }

    const sortedChanges = contractChangeList.sort((a, b) => b.month - a.month)

    // blocks are created in reverse, starting in december since contract changes have no to date
    for (let i = 11; i >= 0; i--) {
      let currentChange = sortedChanges[0]
      if (sortedChanges.length !== 1 && currentChange.month > i) {
        sortedChanges.shift()
        currentChange = sortedChanges[0]
        calculationBlockList.push({
          month: i,
          year: referenceYear,
          amount: currentChange.amount / 12,
          salaryAmount: currentChange.salaryAmount / 12,
          changeStart: currentChange.changeStart,
          changeEnd: currentChange.changeEnd,
          annualAmount: currentChange.annualAmount,
        })
      } else if (currentChange.month <= i) {
        calculationBlockList.push({
          month: i,
          year: referenceYear,
          amount: currentChange.amount / 12,
          salaryAmount: currentChange.salaryAmount / 12,
          changeStart: currentChange.changeStart,
          changeEnd: currentChange.changeEnd,
          annualAmount: currentChange.annualAmount,
        })
      }
    }
  }
  return calculationBlockList
}

const filterAndTransformContractChanges = (
  employeeTimezone: string,
  contractChangeList: ContractChangeFragment[] | undefined = [],
  currentSalary: number,
  referenceYear: number,
) =>
  contractChangeList.reduce((monthsWithAmounts: FullAmountAndMonthAndYear[], contractChange: ContractChangeFragment) => {
    const changes = JSON.parse(contractChange.changes)
    const amount: number = changes.bonus
    const salaryAmount: number = changes.salary ? changes.salary : currentSalary
    const fromDate = moment(contractChange.validFrom)
    const inMeetingYear = fromDate.year() === referenceYear || fromDate.year() === referenceYear - 1

    if (amount >= 0 && salaryAmount >= 0 && inMeetingYear) {
      const change: FullAmountAndMonthAndYear = {
        month: fromDate.month(),
        year: fromDate.year(),
        amount,
        salaryAmount,
        changeStart: moment.tz(`${fromDate.year()}-${fromDate.month() + 1}-${fromDate.date()}`, 'YYYY-M-D', employeeTimezone).toISOString(),
        annualAmount: amount,
      }
      return [...monthsWithAmounts, change]
    }
    return monthsWithAmounts
  }, [])

const createBlocksFromContractBonus = (employeeTimezone: string, contractBonusList: TargetBonusOfYearFragment[], year: number) => {
  // filter correct list entries
  const hasNotEndedPreviously = (val: TargetBonusOfYearFragment): boolean =>
    val.toDate === null || moment.utc(val.toDate).tz(employeeTimezone).year() >= year
  const isNotInFuture = (val: TargetBonusOfYearFragment): boolean => moment.utc(val.fromDate).tz(employeeTimezone).year() <= year

  const validTargetBonusList = contractBonusList.filter(hasNotEndedPreviously).filter(isNotInFuture)

  // sort valid changes descending by from date
  const sortedTargetBonusList = validTargetBonusList.sort((a, b) => {
    const momentA = moment.utc(a.fromDate).tz(employeeTimezone)
    const momentB = moment.utc(b.fromDate).tz(employeeTimezone)
    return momentB.isAfter(momentA) ? 1 : momentA.isAfter(momentB) ? -1 : 0
  })

  const blockList: FullAmountAndMonthAndYear[] = []
  if (sortedTargetBonusList.length > 0) {
    // blocks are created in reverse, starting in december
    // create blocks for all month start and end date criteria will be handled later
    for (let i = 11; i >= 0; i--) {
      let currentChange = sortedTargetBonusList[0]
      const parsedFromDate = moment.utc(currentChange.fromDate).tz(employeeTimezone)
      const parsedToDate = currentChange.toDate ? moment.utc(currentChange.toDate).tz(employeeTimezone) : undefined

      const cleanedFromDate = moment.utc([parsedFromDate.year(), parsedFromDate.month()]).tz(employeeTimezone)
      const cleanedToDate = parsedToDate ? moment.utc([parsedToDate.year(), parsedToDate.month()]).tz(employeeTimezone) : undefined
      const currentBlockMonthDate = moment.utc([year, i]).tz(employeeTimezone)
      const fromDateIsSmallerThanCurrentMonth = cleanedFromDate.isSameOrBefore(currentBlockMonthDate)

      const toDateGreaterThanCurrentMonth = cleanedToDate ? cleanedToDate.isSameOrAfter(currentBlockMonthDate) : true

      if (sortedTargetBonusList.length !== 1 && !fromDateIsSmallerThanCurrentMonth) {
        sortedTargetBonusList.shift()
        currentChange = sortedTargetBonusList[0]
        blockList.push({
          year,
          month: i,
          amount: currentChange.amount / 12,
          salaryAmount: 0,
          changeStart: currentChange.fromDate,
          changeEnd: currentChange.toDate,
          annualAmount: currentChange.amount,
        })
      } else if (sortedTargetBonusList.length !== 0 && fromDateIsSmallerThanCurrentMonth && toDateGreaterThanCurrentMonth) {
        blockList.push({
          year,
          month: i,
          amount: currentChange.amount / 12,
          salaryAmount: 0,
          changeStart: currentChange.fromDate,
          changeEnd: currentChange.toDate,
          annualAmount: currentChange.amount,
        })
      }
    }
  }

  return blockList
}

const mergeSalaryToBlocks = (
  employeeTimezone: string,
  contractBonusBlockList: FullAmountAndMonthAndYear[],
  salaryList: SalaryOfYearFragment[],
  year: number,
): FullAmountAndMonthAndYear[] => {
  const hasNotEndedPreviously = (val: SalaryOfYearFragment): boolean =>
    val.toDate === null || moment.utc(val.toDate).tz(employeeTimezone).year() >= year
  const isNotInFuture = (val: SalaryOfYearFragment): boolean => moment.utc(val.fromDate).tz(employeeTimezone).year() <= year
  const orderByDateDesc = (a: SalaryOfYearFragment, b: SalaryOfYearFragment) => {
    const momentA = moment.utc(a.fromDate).tz(employeeTimezone)
    const momentB = moment.utc(b.fromDate).tz(employeeTimezone)
    return momentB.isAfter(momentA) ? 1 : momentA.isAfter(momentB) ? -1 : 0
  }

  const sortedSalaryList = salaryList.filter(hasNotEndedPreviously).filter(isNotInFuture).sort(orderByDateDesc)

  const blockList: FullAmountAndMonthAndYear[] = []
  if (sortedSalaryList.length > 0) {
    for (const contractBonusBlock of contractBonusBlockList) {
      let currentSalaryChange = sortedSalaryList[0]

      const parsedFromDate = moment.utc(currentSalaryChange.fromDate).tz(employeeTimezone)
      const parsedToDate = currentSalaryChange.toDate ? moment.utc(currentSalaryChange.toDate).tz(employeeTimezone) : undefined

      const cleanedFromDate = moment.utc([parsedFromDate.year(), parsedFromDate.month()]).tz(employeeTimezone)
      const cleanedToDate = parsedToDate ? moment.utc([parsedToDate.year(), parsedToDate.month()]).tz(employeeTimezone) : undefined
      const currentBlockMonthDate = moment.utc([year, contractBonusBlock.month]).tz(employeeTimezone)
      const fromDateIsSmallerThanCurrentMonth = cleanedFromDate.isSameOrBefore(currentBlockMonthDate)

      const toDateGreaterThanCurrentMonth = cleanedToDate ? cleanedToDate.isSameOrAfter(currentBlockMonthDate) : true

      if (sortedSalaryList.length !== 1 && !fromDateIsSmallerThanCurrentMonth) {
        sortedSalaryList.shift()
        currentSalaryChange = sortedSalaryList[0]
        blockList.push({
          year,
          month: contractBonusBlock.month,
          amount: contractBonusBlock.amount,
          salaryAmount: currentSalaryChange.amount / 12,
          changeStart: contractBonusBlock.changeStart,
          changeEnd: contractBonusBlock.changeEnd,
          annualAmount: contractBonusBlock.annualAmount,
        })
      } else if (sortedSalaryList.length !== 0 && fromDateIsSmallerThanCurrentMonth && toDateGreaterThanCurrentMonth) {
        blockList.push({
          year,
          month: contractBonusBlock.month,
          amount: contractBonusBlock.amount,
          salaryAmount: currentSalaryChange.amount / 12,
          changeStart: contractBonusBlock.changeStart,
          changeEnd: contractBonusBlock.changeEnd,
          annualAmount: contractBonusBlock.annualAmount,
        })
      }
    }
  }
  return blockList
}

const convertTargetBonusAmountsToCurrentContractOne = (
  targetBonusList: TargetBonusOfYearFragment[],
  currencyConversionData: CurrencyConversionDto[],
  currentContractCurrency: Currency,
): TargetBonusOfYearFragment[] => {
  const res: TargetBonusOfYearFragment[] = []
  for (const bonus of targetBonusList) {
    const conversionRate: number | undefined = retrieveCurrencyConversionRate(
      bonus.currency,
      currentContractCurrency,
      currencyConversionData,
    )
    if (conversionRate) {
      res.push({ ...bonus, amount: Number((bonus.amount * conversionRate).toFixed(2)) })
    } else {
      res.push({ ...bonus })
    }
  }
  return res
}
